import { useEffect, useRef, useState } from "react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CalendarBlankIcon,
  ClockIcon,
  CrosshairIcon,
  MapPinIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { sanitizarTexto } from "@encuentrayayuda/shared/lib/sanitizar";
import AvisoDireccionDesalineada from "./AvisoDireccionDesalineada.jsx";
import { esMomentoPasado } from "@encuentrayayuda/shared/lib/fechas";
import { buscarDireccion } from "./lib/geocodificacion.js";
import ubicacionPerdida from "../../TALLER/ubicacion.webp";

/// Centro de Pereira, Risaralda. Es el punto de partida del mapa cuando el
/// usuario todavia no ha marcado nada ni dado permiso de ubicacion.
const CENTRO_INICIAL = [4.8087, -75.6906];

const variantes = {
  encontrada: {
    latitud: "latEncontro",
    longitud: "lngEncontro",
    fecha: "fechaHallazgo",
    hora: "horaHallazgo",
    titulo: <>¿Dónde encontraste<br />a la mascota?</>,
    descripcion: "La ubicación exacta ayuda a que su familia pueda encontrarla más rápido.",
    leyendaMapa: "Ubicación exacta",
    direccionFaltante: "Dirección o referencia del hallazgo",
    mostrarPuntoReferencia: true,
  },
  perdida: {
    latitud: "latPerdido",
    longitud: "lngPerdido",
    fecha: "fechaPerdida",
    hora: "horaPerdida",
    titulo: <>¿Dónde y cuándo<br />se perdió?</>,
    descripcion: "La ubicación y el momento son clave para encontrar a tu mascota.",
    leyendaMapa: "Lugar donde se perdió",
    direccionFaltante: "Dirección o referencia donde se perdió",
    mostrarPuntoReferencia: false,
    imagen: ubicacionPerdida,
  },
};

export function MapaUbicacion({ latitud, longitud, precision, onLocation, etiqueta }) {
  const elementoRef = useRef(null);
  const mapaRef = useRef(null);
  const marcadorRef = useRef(null);
  const precisionRef = useRef(null);

  useEffect(() => {
    if (!elementoRef.current || mapaRef.current) return undefined;

    const posicionInicial = latitud && longitud ? [latitud, longitud] : CENTRO_INICIAL;
    const mapa = L.map(elementoRef.current, {
      center: posicionInicial,
      zoom: latitud && longitud ? 17 : 13,
      zoomControl: true,
      attributionControl: true,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "© OpenStreetMap",
    }).addTo(mapa);

    mapa.on("click", ({ latlng }) => {
      onLocation({ latitud: latlng.lat, longitud: latlng.lng, precision: null });
    });
    mapaRef.current = mapa;

    return () => {
      mapa.remove();
      mapaRef.current = null;
    };
    // Sin dependencias a proposito: Leaflet se inicializa UNA vez. Incluir
    // latitud/longitud recrearia el mapa entero en cada clic; el segundo
    // useEffect es el que mueve el marcador.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const mapa = mapaRef.current;
    if (!mapa || latitud == null || longitud == null) return;

    const posicion = [latitud, longitud];
    const icono = L.divIcon({
      className: "location-marker-wrap",
      html: '<span class="location-marker"><span></span></span>',
      iconSize: [44, 52],
      iconAnchor: [22, 49],
    });

    if (marcadorRef.current) marcadorRef.current.setLatLng(posicion);
    else marcadorRef.current = L.marker(posicion, { icon: icono }).addTo(mapa);

    if (precisionRef.current) precisionRef.current.remove();
    if (precision) {
      precisionRef.current = L.circle(posicion, {
        radius: precision,
        color: "#168b58",
        fillColor: "#35b87a",
        fillOpacity: 0.12,
        weight: 1,
      }).addTo(mapa);
    }
    mapa.setView(posicion, 17);
  }, [latitud, longitud, precision]);

  return <div className="location-map" ref={elementoRef} aria-label={etiqueta} />;
}

function UbicacionMascotaForm({ datos, onChange, onBack, onNext, onMissing, variante = "encontrada" }) {
  const [estadoGps, setEstadoGps] = useState("inactivo");
  const [mensajeGps, setMensajeGps] = useState("");
  const solicitudDireccionRef = useRef(0);
  const configuracion = variantes[variante] ?? variantes.encontrada;

  function guardarUbicacion({ latitud, longitud, precision }) {
    onChange((actuales) => ({
      ...actuales,
      [configuracion.latitud]: Number(latitud.toFixed(7)),
      [configuracion.longitud]: Number(longitud.toFixed(7)),
      precisionGps: precision ? Math.round(precision) : null,
    }));
    setEstadoGps("listo");
    setMensajeGps(precision ? `Ubicación obtenida con una precisión aproximada de ${Math.round(precision)} m.` : "Ubicación seleccionada en el mapa.");
  }

  async function completarDireccion(latitud, longitud) {
    const solicitudActual = solicitudDireccionRef.current + 1;
    solicitudDireccionRef.current = solicitudActual;
    try {
      setMensajeGps("Ubicación obtenida. Buscando la dirección…");
      const { direccion, ciudad, departamento } = await buscarDireccion(latitud, longitud);
      if (solicitudActual !== solicitudDireccionRef.current) return;
      if (!direccion) {
        setMensajeGps("Ubicación guardada. No encontramos una dirección cercana; puedes escribir una referencia.");
        return;
      }
      onChange((actuales) => ({
        ...actuales,
        direccionHallazgo: sanitizarTexto(direccion).slice(0, 150),
        // Copia de referencia: si el texto se edita despues y difiere de esta,
        // la ciudad guardada ya no corresponde a lo escrito.
        direccionGeocodificada: sanitizarTexto(direccion).slice(0, 150),
        // Ciudad y departamento alimentan la línea "Pereira, Risaralda" de las
        // tarjetas del listado. Van aparte de la dirección larga a propósito:
        // el listado no manda los 150 caracteres del display_name.
        ciudad: ciudad ? sanitizarTexto(ciudad).slice(0, 80) : null,
        departamento: departamento ? sanitizarTexto(departamento).slice(0, 60) : null,
      }));
      setMensajeGps("Ubicación y dirección obtenidas correctamente.");
    } catch {
      if (solicitudActual !== solicitudDireccionRef.current) return;
      setMensajeGps("Ubicación guardada. No pudimos obtener la dirección; puedes escribir una referencia.");
    }
  }

  function seleccionarUbicacion(ubicacion) {
    guardarUbicacion(ubicacion);
    completarDireccion(ubicacion.latitud, ubicacion.longitud);
  }

  function usarUbicacionActual() {
    if (!navigator.geolocation) {
      setEstadoGps("error");
      setMensajeGps("Este navegador no permite obtener la ubicación. Puedes marcarla directamente en el mapa.");
      return;
    }

    setEstadoGps("cargando");
    setMensajeGps("Buscando tu ubicación…");
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        seleccionarUbicacion({ latitud: coords.latitude, longitud: coords.longitude, precision: coords.accuracy });
      },
      (error) => {
        const denegado = error.code === error.PERMISSION_DENIED;
        setEstadoGps("error");
        setMensajeGps(denegado ? "El permiso de ubicación está desactivado. Actívalo en el navegador o marca el punto en el mapa." : "No pudimos obtener la ubicación. Intenta de nuevo o marca el punto en el mapa.");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  }

  function actualizar(campo, valor) {
    const camposTexto = ["direccionHallazgo", "puntoReferencia"];
    const valorSeguro = camposTexto.includes(campo)
      ? sanitizarTexto(valor, { recortar: false })
      : valor;
    onChange({ ...datos, [campo]: valorSeguro });
  }

  function continuar(evento) {
    evento.preventDefault();
    const direccion = sanitizarTexto(datos.direccionHallazgo);
    const puntoReferencia = sanitizarTexto(datos.puntoReferencia);
    onChange({ ...datos, direccionHallazgo: direccion, puntoReferencia });
    const faltantes = [];
    if (datos[configuracion.latitud] == null || datos[configuracion.longitud] == null) faltantes.push("Ubicación exacta en el mapa");
    if (!direccion) faltantes.push(configuracion.direccionFaltante);
    // El backend rechaza cualquier momento futuro. Atajarlo aca convierte un
    // 400 al final del formulario en un aviso en el paso donde esta el campo.
    if (!esMomentoPasado(datos[configuracion.fecha], datos[configuracion.hora])) {
      faltantes.push("Una fecha y hora que ya hayan pasado");
    }
    if (faltantes.length) {
      onMissing(faltantes);
      return;
    }
    onNext();
  }

  return (
    <section className={`location-step location-step--${variante}`} aria-labelledby="location-title">
      <div className="location-step__intro">
        <div><h2 id="location-title">{configuracion.titulo}</h2><p>{configuracion.descripcion}</p></div>
        {configuracion.imagen && <img src={configuracion.imagen} alt="Ilustración de un punto sobre el mapa" />}
      </div>

      <form className="location-form" onSubmit={continuar}>
        <fieldset>
          <legend>{configuracion.leyendaMapa}</legend>
          <MapaUbicacion
            latitud={datos[configuracion.latitud]}
            longitud={datos[configuracion.longitud]}
            precision={datos.precisionGps}
            onLocation={seleccionarUbicacion}
            etiqueta={`Mapa para seleccionar ${configuracion.leyendaMapa.toLowerCase()}`}
          />
          <input type="hidden" name={configuracion.latitud} value={datos[configuracion.latitud] ?? ""} />
          <input type="hidden" name={configuracion.longitud} value={datos[configuracion.longitud] ?? ""} />
          <button className="current-location-button" type="button" onClick={usarUbicacionActual} disabled={estadoGps === "cargando"}>
            <CrosshairIcon weight="bold" />
            {estadoGps === "cargando" ? "Obteniendo ubicación…" : "Usar mi ubicación actual"}
          </button>
          {mensajeGps && <p className={`gps-message gps-message--${estadoGps}`} role="status">{estadoGps === "error" ? <WarningCircleIcon weight="bold" /> : <MapPinIcon weight="fill" />}{mensajeGps}</p>}
        </fieldset>

        <label>Dirección o referencia
          <textarea maxLength="150" rows="3" value={datos.direccionHallazgo} onChange={(e) => actualizar("direccionHallazgo", e.target.value)} placeholder="Ej: Calle 10 # 45-20, cerca del parque central…" />
          <AvisoDireccionDesalineada
            textoActual={datos.direccionHallazgo}
            textoGeocodificado={datos.direccionGeocodificada}
            ciudad={datos.ciudad}
            onUbicar={(encontrada) => {
              guardarUbicacion({ latitud: encontrada.latitud, longitud: encontrada.longitud, precision: null });
              onChange((actuales) => ({
                ...actuales,
                direccionHallazgo: encontrada.direccion.slice(0, 150),
                direccionGeocodificada: encontrada.direccion.slice(0, 150),
                ciudad: encontrada.ciudad || null,
                departamento: encontrada.departamento || null,
              }));
            }}
          />
          <small>{datos.direccionHallazgo.length}/150</small>
        </label>

        {configuracion.mostrarPuntoReferencia && <label>Punto de referencia <span>(opcional)</span>
          <textarea maxLength="150" rows="3" value={datos.puntoReferencia} onChange={(e) => actualizar("puntoReferencia", e.target.value)} placeholder="Ej: al lado de la farmacia, cerca del semáforo…" />
          <small>{datos.puntoReferencia.length}/150</small>
        </label>}

        <fieldset className="date-time-fields">
          <legend>Fecha y hora aproximada</legend>
          <label><CalendarBlankIcon weight="bold" /><span>Fecha<input type="date" max={datos.fechaMaxima} value={datos[configuracion.fecha]} onChange={(e) => actualizar(configuracion.fecha, e.target.value)} required /></span></label>
          <label><ClockIcon weight="bold" /><span>Hora<input type="time" value={datos[configuracion.hora]} onChange={(e) => actualizar(configuracion.hora, e.target.value)} required /></span></label>
        </fieldset>

        <div className="location-form__actions">
          <button type="button" onClick={onBack}><ArrowLeftIcon weight="bold" /> Atrás</button>
          <button type="submit">Siguiente <ArrowRightIcon weight="bold" /></button>
        </div>
      </form>
    </section>
  );
}

export default UbicacionMascotaForm;
